<template>
    <div class="h-full relative p-6 lg:p-12">
        <div
            class="absolute inset-0 z-20 bg-gray-500/90 transition-opacity"
            v-if="!step3Complete"
        ></div>
        <div class="absolute inset-0 z-20" v-if="!step3Complete">
            <div class="absolute inset-0 z-20 w-100 overflow-y-auto">
                <div
                    class="flex min-h-full items-center justify-center p-4 text-center sm:p-0"
                >
                    <div
                        class="relative transform overflow-hidden rounded-lg bg-white dark:bg-slate-800 px-4 pb-4 pt-5 text-left shadow-xl dark:shadow-slate-900/50 transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6"
                    >
                        <div>
                            <div
                                class="mx-auto flex items-center justify-center"
                            >
                                <span class="fa-stack relative z-20 fa-lg">
                                    <i
                                        class="fa-solid fa-circle fa-stack-2x text-red-100"
                                    ></i>
                                    <i
                                        class="fa-regular fa-exclamation fa-stack-1x fa-inverse text-red-700"
                                    ></i>
                                </span>
                            </div>
                            <div class="mt-3 text-center sm:mt-5">
                                <h3
                                    class="text-base font-semibold leading-6 text-gray-900 dark:text-gray-100"
                                    id="modal-title"
                                >
                                    Go Back to the Previous Step
                                </h3>
                                <div class="mt-2">
                                    <p class="text-sm text-gray-500 dark:text-gray-400">
                                        You must complete the previous step
                                        before continuing.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div v-if="!stepComplete">
            <div class="rounded-lg bg-white dark:bg-slate-800 p-6 shadow-lg dark:shadow-slate-900/50 mb-12">
                <h3 class="text-lg xl:text-xl font-semibold leading-6 mb-3">
                    Step 4: Open Your Second Southwest Credit Card
                    <span class="fa-stack">
                        <i
                            class="fa-solid fa-circle fa-stack-2x text-orange-100"
                        ></i>
                        <i
                            class="fa-regular fa-clipboard-list fa-stack-1x fa-inverse text-orange-700"
                        ></i>
                    </span>
                </h3>

                <p class="text-base mb-3">
                    Your next step in your Companion Pass journey will be to
                    open your 2nd Southwest Credit Card.
                </p>
                <p class="text-base mb-3" v-if="step1?.cards[0].title">
                    Since you started with a
                    <span class="font-semibold">
                        {{ step1?.cards[0].title }}
                    </span>
                    first, your next move is to open one of the
                    {{ nextCardTypeContent }} Southwest Credit Cards:
                </p>
            </div>
            <div
                v-for="(nextCard, nIndex) in nextCardsSet"
                :key="`nextcard-${nIndex}`"
            >
                <div
                    class="rounded-lg bg-white dark:bg-slate-800 px-4 py-5 shadow-lg dark:shadow-slate-900/50 flex flex-row gap-6 items-center mb-3"
                >
                    <div class="w-1/3">
                        <img
                            :src="`${cdnUrl}/${nextCard.card_image?.path}/_small/${nextCard.card_image?.file_name}`"
                            class="w-72"
                        />
                    </div>
                    <div class="w-2/3">
                        <h4 class="font-semibold mb-3">
                            {{ nextCard.title }}
                        </h4>
                        <div
                            class="text-sm mb-3 [&>*>h4]:!text-sm [&>*>h4>span]:!text-sm [&>*>p>span]:!text-sm"
                            v-safe-html="nextCard.welcome_offer"
                        ></div>
                        <a
                            :href="nextCard.application_link"
                            class="btn btn-orange !ml-0 !text-md"
                            target="_blank"
                            :id="`hs-companion-pass-step4-apply-${nIndex}`"
                        >
                            Learn How to Apply
                            <i class="fa-light fa-arrow-right ml-2"></i>
                        </a>
                    </div>
                </div>
                <div
                    class="py-3 text-center mb-2"
                    v-if="nIndex + 1 < nextCardsSet.length"
                >
                    <h3 class="font-semibold text-xl">Or</h3>
                </div>
            </div>
            <div
                v-show="showHidden"
                v-for="(nextCard, hIndex) in hiddenCards"
                :key="`nextcard-${hIndex}`"
            >
                <div class="py-3 text-center mb-2">
                    <h3 class="font-semibold text-xl">Or</h3>
                </div>
                <div
                    class="rounded-lg bg-white dark:bg-slate-800 px-4 py-5 shadow-lg dark:shadow-slate-900/50 flex flex-row gap-6 items-center mb-3"
                >
                    <div class="w-1/3">
                        <img
                            :src="`${cdnUrl}/${nextCard.card_image?.path}/_small/${nextCard.card_image?.file_name}`"
                            class="w-72"
                        />
                    </div>
                    <div class="w-2/3">
                        <h4 class="font-semibold mb-3">
                            {{ nextCard.title }}
                        </h4>
                        <div
                            class="!text-sm mb-3 [&>*>h4]:!text-sm [&>*>h4>span]:!text-sm [&>*>p>span]:!text-sm"
                            v-safe-html="nextCard.welcome_offer"
                        ></div>
                        <a
                            :href="nextCard.application_link"
                            class="btn btn-orange !ml-0 !text-md"
                            target="_blank"
                            :id="`hs-companion-pass-step4-apply-${nIndex}`"
                        >
                            Learn How to Apply
                            <i class="fa-light fa-arrow-right ml-2"></i>
                        </a>
                    </div>
                </div>
            </div>
            <div
                v-if="hiddenCards.length > 0"
                class="text-center absolute bottom-1 m-auto left-0 right-0"
            >
                <button
                    @click="showHidden = !showHidden"
                    class="btn btn-secondary !w-24 md:!w-48 !bg-gray-50 dark:bg-slate-900/40"
                    id="hs-companion-pass-step4-toggle-hidden"
                >
                    <i class="fa-regular fa-eye mr-2"></i>
                    {{ showHidden ? 'Hide' : 'Show' }} Hidden Cards
                </button>
            </div>
        </div>
        <div v-if="stepComplete">
            <div class="rounded-lg bg-white dark:bg-slate-800 p-6 shadow-lg dark:shadow-slate-900/50 mb-12">
                <h3 class="text-lg xl:text-xl font-semibold leading-6 mb-3">
                    Step 4 Complete
                    <span class="fa-stack">
                        <i
                            class="fa-solid fa-circle fa-stack-2x text-green-100"
                        ></i>
                        <i
                            class="fa-regular fa-check fa-stack-1x fa-inverse text-green-700"
                        ></i>
                    </span>
                </h3>
                <p class="text-base mb-3">
                    You've taken the next big step in earning your Companion
                    Pass by opening your
                    <span class="font-semibold">
                        {{ step?.cards[1].title }}.
                    </span>
                </p>
            </div>
            <OpenCard :card="step?.cards[1]" :account="step?.accounts[1]" />
        </div>
    </div>
</template>
<script>
import { mapState, mapActions } from 'pinia';
import { useCompanionPassStore } from '@/store/companionpass';
import OpenCard from '@/components/companion-pass/cards/open-card.vue';
export default {
    name: 'StepFour',
    props: {},
    components: {
        OpenCard,
    },
    data: () => ({
        showHidden: false,
    }),
    computed: {
        ...mapState(useCompanionPassStore, ['completeSteps']),
        cdnUrl() {
            return window.cdnUrl;
        },
        stepContent() {
            return this.getStepContent(4);
        },
        step1Content() {
            return this.getStepContent(1);
        },
        step() {
            return this.getStep(4);
        },
        step1() {
            return this.getStep(1);
        },
        step3() {
            return this.getStep(3);
        },
        stepComplete() {
            return 4 in this.completeSteps ? true : false;
        },
        step3Complete() {
            return 3 in this.completeSteps ? true : false;
        },
        nextCardType() {
            return this.step1?.cards[0].type;
        },
        nextCardTypeContent() {
            return this.step1?.cards[0].type === 'personal'
                ? 'business'
                : 'personal';
        },
        nextCardsSet() {
            let cards = [];
            const openedCardId = this.step1?.cards[0].id;
            if (this.step1Content) {
                if (this.nextCardType === 'personal') {
                    if (
                        this.step1Content.business_cards.length > 0 &&
                        openedCardId !==
                            this.step1Content.business_cards[0].id &&
                        !this.step1Content.business_cards[0].hidden
                    ) {
                        cards.push(this.step1Content.business_cards[0]);
                    }
                    if (
                        this.step1Content.business_cards.length > 1 &&
                        openedCardId !==
                            this.step1Content.business_cards[1].id &&
                        !this.step1Content.business_cards[1].hidden
                    ) {
                        cards.push(this.step1Content.business_cards[1]);
                    }
                }
                if (this.nextCardType === 'business') {
                    if (
                        this.step1Content.personal_cards.length > 0 &&
                        openedCardId !==
                            this.step1Content.personal_cards[0].id &&
                        !this.step1Content.personal_cards[0].hidden
                    ) {
                        cards.push(this.step1Content.personal_cards[0]);
                    }
                    if (
                        this.step1Content.personal_cards.length > 1 &&
                        openedCardId !==
                            this.step1Content.personal_cards[1].id &&
                        !this.step1Content.personal_cards[1].hidden
                    ) {
                        cards.push(this.step1Content.personal_cards[1]);
                    }
                    if (
                        this.step1Content.personal_cards.length > 2 &&
                        openedCardId !==
                            this.step1Content.personal_cards[2].id &&
                        !this.step1Content.personal_cards[2].hidden
                    ) {
                        cards.push(this.step1Content.personal_cards[2]);
                    }
                }
                if (!this.nextCardType) {
                    if (
                        this.step1Content.business_cards.length > 0 &&
                        openedCardId !==
                            this.step1Content.business_cards[0].id &&
                        !this.step1Content.business_cards[0].hidden
                    ) {
                        cards.push(this.step1Content.business_cards[0]);
                    }
                    if (
                        this.step1Content.business_cards.length > 1 &&
                        openedCardId !==
                            this.step1Content.business_cards[1].id &&
                        !this.step1Content.business_cards[1].hidden
                    ) {
                        cards.push(this.step1Content.business_cards[1]);
                    }
                }
            }
            return cards;
        },
        hiddenCards() {
            let cards = [];
            const openedCardId = this.step1?.cards[0].id;
            if (this.step1Content) {
                if (this.nextCardType === 'personal') {
                    if (
                        this.step1Content.personal_cards.length > 0 &&
                        (openedCardId ===
                            this.step1Content.personal_cards[0].id ||
                            this.step1Content.personal_cards[0].hidden)
                    ) {
                        cards.push(this.step1Content.personal_cards[0]);
                    }
                    if (
                        this.step1Content.personal_cards.length > 1 &&
                        (openedCardId ===
                            this.step1Content.personal_cards[1].id ||
                            this.step1Content.personal_cards[1].hidden)
                    ) {
                        cards.push(this.step1Content.personal_cards[1]);
                    }
                    if (
                        this.step1Content.personal_cards.length > 2 &&
                        (openedCardId ===
                            this.step1Content.personal_cards[2].id ||
                            this.step1Content.personal_cards[2].hidden)
                    ) {
                        cards.push(this.step1Content.personal_cards[2]);
                    }
                }
                if (this.nextCardType === 'business') {
                    if (
                        this.step1Content.business_cards.length > 0 &&
                        (openedCardId ===
                            this.step1Content.business_cards[0].id ||
                            this.step1Content.business_cards[0].hidden)
                    ) {
                        cards.push(this.step1Content.business_cards[0]);
                    }
                    if (
                        this.step1Content.business_cards.length > 1 &&
                        (openedCardId ===
                            this.step1Content.business_cards[1].id ||
                            this.step1Content.business_cards[1].hidden)
                    ) {
                        cards.push(this.step1Content.business_cards[1]);
                    }
                }
            }
            return cards;
        },
    },
    methods: {
        ...mapActions(useCompanionPassStore, ['getStep', 'getStepContent']),
    },
    mounted() {},
};
</script>
